import { Button, Tabs, TabsProps } from "antd";
import { FC, memo, MouseEventHandler, ReactNode, useContext, useEffect, useMemo, useRef } from "react";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa6";
import { TabHashKey, TabPaneContext, useHashTab } from "src/routing/tabHash";
import './TabSticky.less';

const DELTA = 4;

interface Props extends Omit<TabsProps, 'children'> {
  /** URL hash entry to sync the active tab with; derived from nesting by default, `false` disables the sync. */
  hashKey?: TabHashKey | false;
}

/** Sticky antd Tabs; uncontrolled tabs follow the URL hash so they are reachable by link. */
const XmTabsSticky: FC<Props> = ({ hashKey, ...props }) => {
  // a controlled caller may start with an undefined activeKey; switching modes later would remount the tabs
  const synced = hashKey !== false && !('activeKey' in props);
  return synced ? <HashTabs hashKey={hashKey || undefined} {...props} /> : <StickyTabs {...props} />;
};

const HashTabs: FC<Omit<Props, 'hashKey'> & { hashKey?: TabHashKey; }> = ({ hashKey, onChange, ...props }) => {
  const hashTab = useHashTab(props.items?.map(i => i.key), props.defaultActiveKey, hashKey);
  return <StickyTabs {...props} activeKey={hashTab.activeKey} onChange={key => { hashTab.onChange(key); onChange?.(key); }} />;
};

const StickyTabs: FC<Omit<TabsProps, 'children'>> = ({ className, ...props }) => {
  const pane = useContext(TabPaneContext);
  const divRef = useRef<HTMLDivElement>(undefined);
  const leftScrollInterval = useRef(0);
  const rightScrollInterval = useRef(0);

  const onLeftEnter: MouseEventHandler<HTMLElement> = e => {
    leftScrollInterval.current = scrollInterval(-DELTA);
  };

  const onLeftLeave = () => {
    clearInterval(leftScrollInterval.current);
  };

  const onRightEnter = () => {
    rightScrollInterval.current = scrollInterval(DELTA);
  };

  const onRightLeave = () => {
    clearInterval(rightScrollInterval.current);
  };

  const vertical = props.tabPlacement === 'start' || props.tabPlacement === 'end';
  const extra: TabsProps['tabBarExtraContent'] = vertical ? null : {
    left: <Button className="xm-tab-scroll" type="text" title="Scroll tabs left as with mouse wheel" onMouseEnter={onLeftEnter} onMouseLeave={onLeftLeave}><FaChevronLeft /></Button>,
    right: <Button className="xm-tab-scroll" type="text" title="Scroll tabs right as with mouse wheel" onMouseEnter={onRightEnter} onMouseLeave={onRightLeave}><FaChevronRight /></Button>
  };

  function scrollInterval(delta: number) {
    return window.setInterval(() => {
      divRef.current.querySelector(':scope > .xm-tab-sticky > .ant-tabs-nav > .ant-tabs-nav-wrap').dispatchEvent(new WheelEvent('wheel', { deltaX: delta }));
    }, 20);
  }

  // rc-tabs marks the wrap with ping classes while the tabs overflow
  useEffect(() => {
    const parent = divRef.current;
    const wrap = parent?.querySelector<HTMLElement>(':scope > .xm-tab-sticky > .ant-tabs-nav > .ant-tabs-nav-wrap');
    let observer: MutationObserver | undefined;
    if (wrap) {
      const update = () => parent.classList.toggle('xm-tab-overflow', wrap.classList.contains('ant-tabs-nav-wrap-ping-left') || wrap.classList.contains('ant-tabs-nav-wrap-ping-right'));
      update();
      observer = new MutationObserver(update);
      observer.observe(wrap, { attributes: true, attributeFilter: ['class'] });
    }

    return () => {
      observer?.disconnect();
      onLeftLeave();
      onRightLeave();
    };
  }, []);

  // hash-synced tabs inside a pane need to know whether it is the visible one
  const items = props.items?.map(item => ({
    ...item,
    children: <TabPane active={pane.active && (props.activeKey === undefined || item.key === props.activeKey)}>
      {item.children}
    </TabPane>
  }));

  return <div className="xm-tab-sticky-parent" ref={divRef}>
    <Tabs className={"xm-tab-sticky " + (className || '')}
      tabBarExtraContent={extra}
      {...props} items={items} />
  </div>;
};

/**
 * Content of one tab. antd keeps hidden panes mounted, and the parent hands in
 * fresh children on every render, so without this every tab switch re-rendered
 * the graphs of every tab visited so far (seconds of blocked UI on graph-heavy
 * pages). The `active` flag still reaches context consumers inside a hidden
 * pane; its content re-renders once it is shown again.
 */
const TabPane: FC<{ active: boolean; children: ReactNode; }> = ({ active, children }) => {
  const value = useMemo(() => ({ nested: true, active }), [active]);
  return <TabPaneContext.Provider value={value}>
    <PaneBody active={active}>{children}</PaneBody>
  </TabPaneContext.Provider>;
};

const PaneBody = memo<{ active: boolean; children: ReactNode; }>(({ children }) => <>{children}</>,
  (_prev, next) => !next.active);

export default XmTabsSticky;
